/**
 * WorkspacePageHeader - Header section for workspace page
 * Displays user-specific title, creation info, and actions
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import NewWorkspaceDialog from '../NewWorkspaceDialog';

interface WorkspacePageHeaderProps {
  user: any; // Clerk user object
  defaultWorkspace: any; // First or default workspace
  isLoading: boolean;
  onRefresh: () => void;
  onWorkspaceCreated: (workspace: any) => void;
}

const WorkspacePageHeader: React.FC<WorkspacePageHeaderProps> = ({
  user,
  defaultWorkspace,
  isLoading,
  onRefresh,
  onWorkspaceCreated
}) => {
  // Generate user-specific workspace title
  const getWorkspaceTitle = () => {
    if (!user) return 'Workspaces';
    
    // Try to get user's first name, fall back to display name, then username, then email
    const userName = user.firstName || 
                     user.fullName?.split(' ')[0] || 
                     user.username || 
                     user.primaryEmailAddress?.emailAddress?.split('@')[0] || 
                     'Your';
    
    return `${userName}'s Workspaces`;
  };

  return (
    <div className="px-6 py-6 border-b border-border/30">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
        <div>
          <h1 className="text-xl font-medium text-foreground">
            {getWorkspaceTitle()}
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            {defaultWorkspace ? (
              <>Created {formatDistanceToNow(new Date(defaultWorkspace.created_at), { addSuffix: true })}</>
            ) : isLoading ? (
              'Loading workspace information...'
            ) : (
              'No workspaces found'
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            onClick={onRefresh}
            disabled={isLoading}
            size="sm"
            className="text-muted-foreground hover:text-foreground hover:bg-muted/50"
          >
            <span className={`h-4 w-4 mr-1.5 ${isLoading ? 'animate-spin' : ''}`}>⟳</span>
            Refresh
          </Button>
          <NewWorkspaceDialog onWorkspaceCreated={onWorkspaceCreated} />
        </div>
      </div>
    </div>
  );
};

export default WorkspacePageHeader;